import { Track } from "@/src/lib/tracking";
import React, { memo, useCallback } from "react";
import { cn } from "utils";
import { Icon, OspToast, deviceDetector, ospThemes } from "@ospweb/sdk-ui";
// import Image from "next/image";
import { useSnapshot } from "valtio";
import { ProductStatus } from "../../../CurrentPlan/constants";

import { BillingCycle } from "../../constants";
import { PurchaseActionsProps } from "../../types";
import { PayChannel } from "@features/api/memberApi";
import {
  pickCheckoutChannels,
  resolveChannelMeta,
} from "@utils/member/checkoutChannel";
import {
  buildConsentCopy,
  CONSENT_VERSION,
} from "@utils/member/checkoutConsent";
import { resolveOnboardingTrialDays } from "../../onboardingPricingOffers";
import ChannelPayButton from "./ChannelPayButton";
import { CheckoutConsent } from "./CheckoutConsent";
import { hasChannel } from "@hooks/memberPlansData";
import { useSubscriptionList } from "@hooks/useSubscriptionList";
import {
  findChangeablePlan,
  findSubscribedChannel,
  useStartPaymentMethodUpdate,
} from "@hooks/useSubscriptionManage";
import { getRetentionPlanName } from "../../../CurrentPlan/subscriptionRetention/runCancelSubscriptionFlow";
import { openUpgradeConfirmModal } from "../../UpgradeConfirmModal/UpgradeConfirmModalCreateAble";
import type { UpgradeConfirmResult } from "../../UpgradeConfirmModal/UpgradeConfirmModalContent";
import { UpgradeSuccessModalCreateAble } from "@components/Payment/UpgradeSuccessModal/UpgradeSuccessModalCreateAble";
import { getTrialData } from "../../../CurrentPlan/utils/getTrialData";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/Subscription/SubscriptionCard/PurchaseActions/index";
import { UpgradeReminderModalCreateAble } from "@components/modal/UpgradeReminder";
import { rootStore } from "@store/rootStore";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";
import { useAppPurchaseFlow } from "@hooks/useAppPurchaseFlow";
import { useAppPurchaseMemberInvalidate } from "@hooks/useAppPurchaseMemberInvalidate";
import { useShellRouter } from "@hooks/useShellAwareRouter";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 订阅计划购买操作组件
 * App 内购：产品列表由 GET /products 取得，购买前调 payment/check，购买成功后由 Web 调 confirm
 */
const PurchaseActions: React.FC<PurchaseActionsProps> = ({
  isFree,
  userPlanInfo,
  billingCycle,
  plan,
  onChannelCheckout,
  isLoading,
  isH5,
  trialList,
  onAppPurchaseSuccess,
  disableInvisiblePlaceholder = false,
  buttonTitleOverride,
  forceButtonDisabled,
  customCta,
  // hidePaymentIcons = false,
  showCryptoOverride,
  termsFallback,
  className,
}) => {
  const {
    auto_renewal,
    renewal_id,
    isFree: isUserFree,
    isTrial: isUserTrial,
    available_products,
  } = userPlanInfo ?? {};
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const router = useShellRouter();
  const {
    beginAppPurchase,
    completeAppPurchase,
    invalidateMemberQueriesAfterDelay,
  } = useAppPurchaseMemberInvalidate();
  // 用户是否为会员
  const isMember = !isUserFree;
  const autoRenewal = auto_renewal;
  const isInIOS = deviceDetector.isIOSApp();
  const isInApp = deviceDetector.isApp();

  const {
    purchaseInApp,
    isPurchasing: isAppPurchasing,
    shouldTryNativePayment,
  } = useAppPurchaseFlow();
  // 用户当前购买的是试用会员，并且自动续费的产品id和当前产品id相同
  const isPayingPlan = isUserTrial && renewal_id === plan?.id;
  // 当前生效的产品id相同，或者用户当前购买的是试用会员并且自动续费产品id与当前产品id相同，则属于同一个产品
  const isCurrentPlan = userPlanInfo?.product?.id === plan?.id || isPayingPlan;

  // 判断该正价产品是否有对应的试用产品
  const isTrialPlan = trialList?.some(
    (item) =>
      item.renewal_id === plan?.id && item.status === ProductStatus.ACTIVE,
  );
  const trialPrice = trialList?.find(
    (item) =>
      item.renewal_id === plan?.id && item.status === ProductStatus.ACTIVE,
  )?.price;

  // 实际购买产品与渠道可用性：试用场景（非会员 + 套餐有在售试用产品）结的是试用产品，
  // 渠道判断以其 payment_configs 为准 —— 试用产品是独立产品，各渠道价格独立配置
  const trialProduct =
    isTrialPlan && !isMember
      ? getTrialData({ trialList: trialList || [], id: plan?.id })
      : undefined;
  const primaryPurchaseProduct = trialProduct ?? plan;
  const { data: subscriptionItems, isLoading: isSubscriptionListLoading } =
    useSubscriptionList({
      enabled: !!isLoggedIn && !isInApp && isMember,
    });
  // 渠道顺序来自产品 payment_configs：第一个为主按钮，其余为「or …」次级入口（EDG-552）；
  // 已有生效订阅且其渠道仍在售时，该渠道提到主按钮（换渠道下单会被后端跨渠道互斥拦截）
  const { primary: primaryChannel, secondary: secondaryChannels } =
    pickCheckoutChannels(
      primaryPurchaseProduct,
      findSubscribedChannel(subscriptionItems),
    );
  // 试用天数按实际下单渠道解析（STRIPE 读 offer，PAYPAL / AIRWALLEX 读目录），按钮与同意文案共用
  const trialDays =
    trialProduct && primaryChannel
      ? resolveOnboardingTrialDays(trialProduct, primaryChannel)
      : undefined;
  const checkoutPosition = isH5 ? "member_h5" : "member_pc";

  // 一键升级（EDG-552，F10）：当前订阅可原地换档（CHANGE_PLAN）时，Upgrade 弹确认框而不是走收银台
  const changeablePlan = findChangeablePlan(subscriptionItems);
  const matchedAction = (available_products || []).find(
    (item: { product_id?: string }) => item.product_id === plan?.id,
  )?.action;
  // 后端换档用目标产品同渠道的 price（be-design changePlan.targetThirdPriceId），目标未配该渠道则只能走收银台
  const shouldInPlaceUpgrade =
    !isInApp &&
    matchedAction === "UPGRADE" &&
    billingCycle !== BillingCycle.LIFETIME &&
    plan?.pay_cycle !== BillingCycle.LIFETIME &&
    !!plan &&
    !!changeablePlan &&
    hasChannel(plan, changeablePlan.pay_channel as PayChannel);
  const { start: startPaymentMethodUpdate } = useStartPaymentMethodUpdate();

  // 禁用crypto支付 2.8.0 版本
  const showCryptoPayment = false;

  const buttonText = (): {
    buttonTitle: string;
    buttonDisabled: boolean;
    isUpgrade?: boolean;
  } => {
    if (!isLoggedIn) {
      return {
        buttonTitle: I18n.t("Create Account"),
        buttonDisabled: false,
        isUpgrade: false,
      };
    }
    // 当该相同周期的产品可以试用时
    if (!isMember && isTrialPlan) {
      // 非会员状态，展示为立即购买：
      // 点击购买判断是否登录，未登录弹窗登录弹窗，登录后
      // 如果试用会员，则展示为‘~Try $1 Trial~’，按钮可点击
      // 如果非试用会员，则展示为‘~Subscribe~’，按钮可点击
      return {
        // iOS 走 IAP，文案沿用；web 天数取实际下单渠道的试用配置（原写死 7 天，EDG-552）
        buttonTitle: isInIOS
          ? I18n.t("Start with 7 days free trial")
          : !isInApp && trialDays != null
            ? I18n.t("Try %{days} Days for %{var0}", {
                days: trialDays,
                var0: `$${trialPrice}`,
              })
            : I18n.t("Try 7 Days for %{var0}", { var0: `$${trialPrice}` }),
        buttonDisabled: false,
        isUpgrade: false,
      };
    }

    // 当前生效套餐：固定为 Current Plan，且自动续费时按钮禁用
    if (isCurrentPlan && autoRenewal && !isFree) {
      return {
        buttonTitle: I18n.t("Current Plan"),
        buttonDisabled: true,
        isUpgrade: false,
      };
    }

    // 其余情况：根据 available_products 决定按钮文案和状态
    const matchedAvailable = (available_products || []).find(
      (item: { product_id?: string }) => item.product_id === plan?.id,
    );

    if (!matchedAvailable) {
      // 没有匹配到可用产品，表示无法购买，统一为 Contact Us
      return {
        buttonTitle: I18n.t("Contact Us"),
        buttonDisabled: true,
        isUpgrade: true,
      };
    }

    // todo: 上线的时候，需要把升级的入口禁掉，后续升级逻辑一致之后再打开
    switch (matchedAvailable.action) {
      case "PURCHASE":
        return {
          buttonTitle:
            billingCycle === BillingCycle.LIFETIME
              ? I18n.t("Claim Founding Pass")
              : I18n.t("Subscribe"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "RENEW":
        return {
          buttonTitle: I18n.t("Renew"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "UPGRADE":
        return {
          buttonTitle:
            billingCycle === BillingCycle.LIFETIME
              ? I18n.t("Claim Founding Pass")
              : I18n.t("Upgrade"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      case "UPGRADE_TO_LATEST":
        return {
          buttonTitle: I18n.t("Switch to v2 Plan"),
          buttonDisabled: false,
          isUpgrade: false,
        };
      default:
        return {
          buttonTitle: I18n.t("Contact Us"),
          buttonDisabled: false,
          isUpgrade: true,
        };
    }
  };

  const { buttonTitle, buttonDisabled, isUpgrade } = buttonText();
  const baseButtonDisabled =
    typeof forceButtonDisabled === "boolean"
      ? forceButtonDisabled
      : buttonDisabled;
  // web 可下单（非 App、已登录、付费套餐、主按钮本可点击、非 Contact Us）时才进入渠道 / 同意判定
  const isWebCheckout =
    !isInApp &&
    isLoggedIn &&
    !isFree &&
    !customCta &&
    !isUpgrade &&
    !baseButtonDisabled;
  // AC-6：没有任何 web 渠道 → 「Not available」并禁用
  const hasNoWebChannel = isWebCheckout && primaryChannel === null;
  const finalButtonTitle = hasNoWebChannel
    ? I18n.t("Not available")
    : buttonTitleOverride || buttonTitle;
  // 原地升级时同意文案在确认弹窗里展示（cta=Confirm upgrade），卡片上不重复
  const consentCopy =
    isWebCheckout && primaryChannel && plan && !shouldInPlaceUpgrade
      ? buildConsentCopy({
          product: plan,
          trialProduct,
          trialDays,
          ctaLabel: finalButtonTitle,
        })
      : null;
  // 订阅 / 试用必须先展示扣款条款才能下单（终身产品不需要）；条款缺信息（如试用天数未就绪）→ 禁用
  const isConsentMissing =
    isWebCheckout &&
    !shouldInPlaceUpgrade &&
    !!primaryChannel &&
    plan?.pay_cycle !== BillingCycle.LIFETIME &&
    !consentCopy;
  // 订阅列表还没回来时不知道能不能原地换档：此时点 Upgrade 会落到下面的收银台分支，给已有订阅的用户另开一个新订阅
  //（beta 2026-10-14：同一个 Upgrade 按钮有时弹确认框、有时直接跳支付页）。列表到了再放开
  const isUpgradeResolving =
    !isInApp &&
    isSubscriptionListLoading &&
    matchedAction === "UPGRADE" &&
    billingCycle !== BillingCycle.LIFETIME &&
    plan?.pay_cycle !== BillingCycle.LIFETIME;
  const finalButtonDisabled =
    baseButtonDisabled ||
    hasNoWebChannel ||
    isConsentMissing ||
    isUpgradeResolving;
  const consentVersion = consentCopy ? CONSENT_VERSION : undefined;

  const showUpgradeModal = useCallback(async () => {
    await UpgradeReminderModalCreateAble({
      scenario: "enterprise-realTimeReports",
    });
  }, []);

  const handleUpgradeResult = useCallback(
    async (result: UpgradeConfirmResult) => {
      switch (result) {
        case "succeeded":
          await invalidateMemberQueriesAfterDelay();
          await UpgradeSuccessModalCreateAble({ isTrial: false });
          return;
        case "pending":
          OspToast.info({ content: I18n.t("Your upgrade is processing.") });
          await invalidateMemberQueriesAfterDelay();
          return;
        case "declined_update_pm":
          // 被拒后引导换卡，不自动重扣（AC-38）
          if (changeablePlan) {
            await startPaymentMethodUpdate(changeablePlan);
          }
          return;
        default:
          // error：弹窗已 Toast，能力 / 商品已变时 useChangePlan 已刷新；cancelled：无动作
          return;
      }
    },
    [
      invalidateMemberQueriesAfterDelay,
      changeablePlan,
      startPaymentMethodUpdate,
    ],
  );

  const handleClick = useCallback(async () => {
    if (isLoading || finalButtonDisabled || isAppPurchasing) {
      return;
    }
    // isUpgrade
    if (!isLoggedIn) {
      redirectToLoginPage();
      return;
    }
    if (isUpgrade) {
      showUpgradeModal();
      return;
    }
    if (shouldInPlaceUpgrade && changeablePlan && plan) {
      const result = await openUpgradeConfirmModal({
        currentPlan: changeablePlan,
        currentPlanName:
          userPlanInfo?.product?.name ?? getRetentionPlanName(changeablePlan),
        targetProduct: plan,
      });
      await handleUpgradeResult(result);
      return;
    }

    // 触发购买的产品：与渠道可用性判断同口径（试用场景为试用产品）
    const productForPurchase = primaryPurchaseProduct;
    const backendProductId = productForPurchase?.id ?? null;

    if (!backendProductId) {
      Track.log("error", "Member", "[trace][pay] No product ID found");
      return;
    }
    if (!productForPurchase) {
      Track.log(
        "error",
        "Member",
        "[trace][pay] No product found for purchase",
      );
      return;
    }

    if (shouldTryNativePayment) {
      beginAppPurchase();
      const result = await purchaseInApp(productForPurchase, {
        onSuccess: async () => {
          completeAppPurchase();
          await onAppPurchaseSuccess?.(backendProductId);
        },
        onMemberExist: async () => {
          completeAppPurchase();
          await invalidateMemberQueriesAfterDelay();
        },
        onError: (msg) => {
          completeAppPurchase();
          OspToast.error({
            content:
              msg || String(I18n.t("Purchase failed. Please try again.")),
          });
        },
        suppressTimeoutToast: true,
      });
      // App 内缺 IAP 配置 / 原生支付不可用：只提示，不回退 web 收银台（C-1，AC-21/22）
      if (!result.success && result.iapUnavailable) {
        completeAppPurchase();
        OspToast.error({
          content:
            result.error ||
            String(I18n.t("Purchase failed. Please try again.")),
        });
      }
      return;
    }

    if (!primaryChannel) {
      return;
    }
    Track.log("info", "Member", "[trace][pay] channel click", {
      cause: String(backendProductId),
      channel: primaryChannel,
    });
    onChannelCheckout?.(backendProductId, primaryChannel);
  }, [
    isLoading,
    finalButtonDisabled,
    isAppPurchasing,
    isLoggedIn,
    open,
    isUpgrade,
    showUpgradeModal,
    primaryPurchaseProduct,
    onAppPurchaseSuccess,
    onChannelCheckout,
    primaryChannel,
    shouldInPlaceUpgrade,
    changeablePlan,
    plan,
    userPlanInfo,
    handleUpgradeResult,
    shouldTryNativePayment,
    purchaseInApp,
    beginAppPurchase,
    completeAppPurchase,
    invalidateMemberQueriesAfterDelay,
  ]);
  const handleCryptoClick = () => {
    if (isLoading || buttonDisabled) {
      return;
    }
    if (!isLoggedIn) {
      redirectToLoginPage();
      return;
    }
    const payPath = `payment/${plan?.id}`;
    Track.log("info", "Member", "[trace][pay] crypto click", {
      cause: String(plan?.id),
    });
    router.push(payPath);
  };

  const actions = () => {
    // 旧流程（大屏/历史页面）沿用原有 crypto 展示条件。
    const shouldShowLegacyCrypto =
      showCryptoPayment &&
      !isUpgrade &&
      !(autoRenewal && isCurrentPlan) &&
      !isTrialPlan &&
      !!(!autoRenewal && billingCycle === BillingCycle.YEAR);
    // 新流程（移动端定价页）可传入矩阵结果覆盖展示，但仍受审核开关 showCryptoPayment 约束。
    const useCryptoOverride = typeof showCryptoOverride === "boolean";
    const shouldShowCrypto = useCryptoOverride
      ? showCryptoOverride && showCryptoPayment
      : shouldShowLegacyCrypto;

    // 次级渠道入口（「or Pay with …」）：后端顺序里除第一个外的渠道；只有 1 个渠道时不渲染（AC-5）。
    // App 内走内购不展示；升级/当前套餐场景与主按钮口径对齐。
    const shouldShowSecondary =
      !!onChannelCheckout &&
      !!plan &&
      !customCta &&
      isLoggedIn &&
      !isFree &&
      !isUpgrade &&
      !(isCurrentPlan && autoRenewal) &&
      !isInApp &&
      secondaryChannels.length > 0;
    // 同意文案由本组件唯一负责（PC / H5 共用）；不展示时交给外层条款兜底
    const consentNode = consentCopy ? (
      <CheckoutConsent
        product={plan}
        trialProduct={trialProduct}
        trialDays={trialDays}
        ctaLabel={finalButtonTitle}
      />
    ) : (
      termsFallback
    );
    const isPrimaryTracked = isWebCheckout && !!primaryChannel;

    /**
     * 次级渠道入口。试用场景下各渠道试用天数来源不同（STRIPE 读 offer、PAYPAL / AIRWALLEX 读目录）：
     * 与主渠道不同时紧跟展示该渠道自己的条款；该渠道试用未就绪则禁用（与 onboarding 同口径，Review Issue #8）。
     */
    const renderSecondaryChannel = (channel: PayChannel, index: number) => {
      const channelTrialDays = trialProduct
        ? resolveOnboardingTrialDays(trialProduct, channel)
        : undefined;
      const isChannelTrialMissing = !!trialProduct && channelTrialDays == null;
      const hasOwnTrialDays =
        !!trialProduct &&
        channelTrialDays != null &&
        channelTrialDays !== trialDays;
      return (
        <React.Fragment key={channel}>
          <ChannelPayButton
            channel={channel}
            productId={primaryPurchaseProduct?.id}
            product={primaryPurchaseProduct}
            position={checkoutPosition}
            consentVersion={consentVersion}
            primaryChannel={index === 0 ? primaryChannel : undefined}
            disabled={finalButtonDisabled || isLoading || isChannelTrialMissing}
            onClick={() => {
              // or-label: n/a —— label 与 checkout_channel_click 事件已在 ChannelPayButton 内部声明
              // 空守卫与主按钮同口径：id 缺失时不得以空 product_id 下单
              const backendProductId = primaryPurchaseProduct?.id ?? "";
              if (!backendProductId) {
                Track.log(
                  "error",
                  "Member",
                  "[trace][pay] No product ID found",
                );
                return;
              }
              Track.log("info", "Member", "[trace][pay] channel click", {
                cause: backendProductId,
                channel,
              });
              onChannelCheckout?.(backendProductId, channel);
            }}
          />
          {hasOwnTrialDays && plan && (
            <CheckoutConsent
              product={plan}
              trialProduct={trialProduct}
              trialDays={channelTrialDays}
              ctaLabel={
                resolveChannelMeta(
                  channel,
                  primaryPurchaseProduct,
                )?.secondaryLabel() ?? finalButtonTitle
              }
            />
          )}
        </React.Fragment>
      );
    };

    if (customCta) {
      if (!isLoggedIn) {
        return (
          <div className={cn("flex flex-col gap-2")}>
            <button
              type="button"
              data-openreplay-label="member_purchaseactions_click"
              className={cn(
                "button md_button secondary_button w-full button_m",
              )}
              onClick={handleClick}
            >
              {buttonTitle}
            </button>
          </div>
        );
      }

      if (customCta.mode === "hidden") {
        return <div className="h-11" />;
      }

      const isCurrent = customCta.mode === "current";
      return (
        <div className="flex flex-col gap-2 w-full">
          <button
            type="button"
            data-openreplay-label="member_purchaseactionscustom_click"
            className={cn(
              "button md_button w-full button_m",
              isCurrent ? "secondary_button" : "emphasis_button",
            )}
            disabled={isCurrent || customCta.disabled}
            onClick={customCta.onClick}
          >
            {customCta.text}
          </button>
          {/* {shouldShowCrypto ? (
            <div
              className={cn(
                "flex flex-row items-center justify-center body_m text-content_tertiary",
                (isCurrent || customCta.disabled || isLoading) &&
                  "text-content_tertiary/20",
              )}
            >
              <span>{I18n.t("or")}</span>
              <div
                data-openreplay-label="member_renewwithcrypto_click"
                onClick={() => {
                  if (isCurrent || customCta.disabled || isLoading) {
                    return;
                  }
                  handleCryptoClick();
                }}
                className={cn(
                  "md:text-content_brand_primary text-content_accent4_primary hover:text-content_brand_primary/70 cursor-pointer button_m ml-1",
                  (isCurrent || customCta.disabled || isLoading) &&
                    "cursor-not-allowed md:text-content_brand_primary text-content_accent4_primary hover:md:text-content_brand_primary hover:text-content_accent4_primary",
                )}
              >
                {!autoRenewal && isCurrentPlan
                  ? I18n.t("Renew with crypto")
                  : I18n.t("Pay with crypto")}
              </div>
            </div>
          ) : null} */}
        </div>
      );
    }

    if (!isLoggedIn) {
      return (
        <div className={cn("flex flex-col gap-2")}>
          <button
            type="button"
            data-openreplay-label="member_purchaseactions_click"
            className={cn("button md_button secondary_button w-full button_m")}
            onClick={handleClick}
          >
            {buttonTitle}
          </button>
        </div>
      );
    }
    // 区分是免费还是付费
    return isFree ? (
      <div className={cn("flex flex-col gap-2 w-full h-11")}>
        {/* <button
          data-openreplay-label="member_purchaseactions_subscribe"
          disabled
          className={cn(
            "w-full button tonal_button md_button button_m opacity-100",
          )}
        >
          {I18n.t("Current Plan")}
        </button>
        {!disableInvisiblePlaceholder && (
          <div
            className={cn(
              "h-5 invisible",
              !(!autoRenewal && billingCycle === BillingCycle.YEAR) && "hidden",
            )}
          ></div>
        )} */}
      </div>
    ) : (
      <div className="flex flex-col gap-2 w-full">
        <button
          data-openreplay-label="member_purchaseactions_subscribe"
          // 展开写法：仅 web 下单时挂渠道事件（App / Contact Us 等不挂）。click 恒挂 ——
          // 禁用的 <button> 浏览器不派发 click；用条件三元埋点生成器收录不到
          {...(isPrimaryTracked
            ? {
                "data-or-view": "checkout_channel_view",
                "data-or-event": "checkout_channel_click",
                "data-or-param-channel": primaryChannel ?? undefined,
                "data-or-param-productid": primaryPurchaseProduct?.id,
                "data-or-param-position": checkoutPosition,
                "data-or-param-isprimary": "1",
                "data-or-param-consentversion": consentVersion,
              }
            : {})}
          onClick={handleClick}
          disabled={finalButtonDisabled}
          className={cn(
            "w-full button primary_button md_button button_m group",
            {
              tonal_button: finalButtonDisabled,
            },
          )}
          data-loading={(isLoading && isPayingPlan) || isAppPurchasing}
        >
          {!(isLoading && isPayingPlan) && !isAppPurchasing && (
            <div className="contents group-loading:hidden">
              {finalButtonTitle}
              {/* {!isInApp && !hidePaymentIcons && (
                <div
                  className={cn(
                    "items-center justify-center gap-[6px] ml-[6px] hidden",
                    {
                      flex: !isUpgrade && !isCurrentPlan,
                    },
                  )}
                >
                  <Image
                    src={getAssetsUrl("/assets/images/visa-dark.webp")}
                    alt="visa"
                    width={48}
                    height={14}
                  />
                  <Image
                    src={getAssetsUrl("/assets/images/applePay-dark.webp")}
                    alt="applepay"
                    width={23}
                    height={14}
                  />
                  <Image
                    src={getAssetsUrl("/assets/images/googlePay-dark.webp")}
                    alt="googlepay"
                    width={23}
                    height={14}
                  />
                </div>
              )} */}
            </div>
          )}
          <Icon
            name="ic_loading2"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
        {/* 如果试用会员，则不展示or后面的内容, 不能使用crypto购买 */}
        {shouldShowCrypto ? (
          <div
            className={cn(
              "flex flex-row items-center justify-center body_m text-content_tertiary",
              (finalButtonDisabled || isLoading) && "text-content_tertiary/20",
              !isH5 && finalButtonDisabled && !isCurrentPlan && "invisible",
              !useCryptoOverride &&
                isTrialPlan &&
                (disableInvisiblePlaceholder ? "hidden" : "invisible"),
              !useCryptoOverride &&
                !(!autoRenewal && billingCycle === BillingCycle.YEAR) &&
                "hidden",
            )}
          >
            <span>{I18n.t("or")}</span>
            <div
              data-openreplay-label="member_renewwithcrypto_click"
              onClick={handleCryptoClick}
              className={cn(
                "md:text-content_brand_primary text-content_accent4_primary hover:text-content_brand_primary/70 cursor-pointer button_m ml-1",
                (finalButtonDisabled || isLoading) &&
                  "cursor-not-allowed md:text-content_brand_primary text-content_accent4_primary hover:md:text-content_brand_primary hover:text-content_accent4_primary",
              )}
            >
              {!autoRenewal && isCurrentPlan
                ? I18n.t("Renew with crypto")
                : I18n.t("Pay with crypto")}
            </div>
          </div>
        ) : shouldShowSecondary ? (
          secondaryChannels.map(renderSecondaryChannel)
        ) : (
          <>
            {disableInvisiblePlaceholder ? null : (
              <div
                className={cn(
                  "h-5 invisible",
                  !(!autoRenewal && billingCycle === BillingCycle.YEAR) &&
                    "hidden",
                )}
              ></div>
            )}
          </>
        )}
        {consentNode}
      </div>
    );
  };

  if (isLoggedIn && !userPlanInfo) {
    return (
      <div className="flex flex-col md:border-b md:border-border_white_transparent10 md:pb-8">
        <div className={cn("flex flex-col gap-2 w-full")}>
          <button
            type="button"
            disabled
            data-loading={isLoading}
            className={cn("w-full button md_button button_m tonal_button")}
            aria-busy={isLoading}
          />
          {!disableInvisiblePlaceholder ? <div className="h-5" /> : null}
        </div>
      </div>
    );
  }

  return <div className={cn("flex flex-col", className)}>{actions()}</div>;
};

export default memo(PurchaseActions);
